/**
 * useActivities Hook
 *
 * React Query hooks for Activity instances.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/shared/services';
import { useTranslation } from '@/core';
import {
  Activity,
  ActivityFilters,
  ActivityFormData,
  ActivityEnrollment,
  ActivityDeleteResult,
  DeleteMode,
} from '../types';
import {
  fetchActivities,
  fetchAllActivities,
  fetchActivity,
  createActivity,
  updateActivity,
  deleteActivity,
  completeActivity,
  cancelActivity,
  fetchActivityEnrollments,
} from '../services/activityService';

// ── Query key factory ──────────────────────────────────────────────────────────

export const activityKeys = {
  all: ['activities'] as const,
  lists: () => [...activityKeys.all, 'list'] as const,
  list: (filters?: ActivityFilters) => [...activityKeys.lists(), filters] as const,
  details: () => [...activityKeys.all, 'detail'] as const,
  detail: (id: string) => [...activityKeys.details(), id] as const,
  enrollments: (id: string) => [...activityKeys.detail(id), 'enrollments'] as const,
};

// ── Hooks ──────────────────────────────────────────────────────────────────────

export function useActivities(
  filters?: ActivityFilters,
  options?: { enabled?: boolean },
) {
  return useQuery<Activity[]>({
    queryKey: activityKeys.list(filters),
    queryFn: () => fetchActivities(filters),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Every session of a template, across all pages.
 *
 * Used where a partial answer would mislead — e.g. counting how many sessions
 * a scoped delete is about to remove.
 */
export function useAllTemplateActivities(
  templateId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<Activity[]>({
    queryKey: [...activityKeys.lists(), 'all', { templateId }],
    queryFn: () => fetchAllActivities({ templateId: templateId as string }),
    enabled: (options?.enabled ?? true) && !!templateId,
  });
}

export function useActivity(id: string) {
  return useQuery<Activity>({
    queryKey: activityKeys.detail(id),
    queryFn: () => fetchActivity(id),
    enabled: !!id,
  });
}

export function useCreateActivity() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: ActivityFormData) => createActivity(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      notify.success(t('activities.messages.created', 'Activity created successfully'));
    },
    onError: (error: Error) => {
      notify.error(t('activities.messages.createFailed', 'Error creating activity: {{message}}', { message: error.message }));
    },
  });
}

export function useUpdateActivity() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, data, editMode }: { id: string; data: Partial<ActivityFormData>; editMode?: 'single' | 'this_and_future' | 'all' }) =>
      updateActivity(id, data, { editMode }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      queryClient.invalidateQueries({ queryKey: activityKeys.detail(variables.id) });
      notify.success(t('activities.messages.updated', 'Activity updated'));
    },
    onError: (error: Error) => {
      notify.error(t('activities.messages.updateFailed', 'Error updating activity: {{message}}', { message: error.message }));
    },
  });
}

export function useDeleteActivity() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation<ActivityDeleteResult, Error, { id: string; deleteMode?: DeleteMode; cancelEnrollments?: boolean }>({
    mutationFn: ({ id, deleteMode, cancelEnrollments }) =>
      deleteActivity(id, deleteMode, cancelEnrollments),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      const msg = result.deleted === 1
        ? t('activities.messages.deleted', 'Activity deleted')
        : t('activities.messages.deletedMultiple', '{{count}} activities deleted', { count: result.deleted });
      notify.success(msg);
    },
    onError: (error: Error) => {
      notify.error(t('activities.messages.deleteFailed', 'Error deleting activity: {{message}}', { message: error.message }));
    },
  });
}

export function useCompleteActivity() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) => completeActivity(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      queryClient.invalidateQueries({ queryKey: activityKeys.detail(id) });
      notify.success(t('activities.messages.completed', 'Activity completed'));
    },
    onError: (error: Error) => {
      notify.error(t('activities.messages.completeFailed', 'Error completing activity: {{message}}', { message: error.message }));
    },
  });
}

export function useCancelActivity() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, reason, approverPassword, approverNfcUid }: {
      id: string;
      reason?: string;
      approverPassword?: string;
      approverNfcUid?: string;
    }) => cancelActivity(id, reason, { approverPassword, approverNfcUid }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      queryClient.invalidateQueries({ queryKey: activityKeys.detail(variables.id) });
      notify.success(t('activities.messages.cancelled', 'Activity cancelled'));
    },
    onError: (error: Error) => {
      notify.error(t('activities.messages.cancelFailed', 'Error cancelling activity: {{message}}', { message: error.message }));
    },
  });
}

export function useActivityEnrollments(activityId: string) {
  return useQuery<ActivityEnrollment[]>({
    queryKey: activityKeys.enrollments(activityId),
    queryFn: () => fetchActivityEnrollments(activityId),
    enabled: !!activityId,
  });
}
